import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { oneTimesUpdate } from "@domain/coupons"
import { yupResolver } from "@hookform/resolvers/yup"
import useControlledForm from "@hooks/use_controlled_form"
import useTranslate from "@hooks/use_translate"
import { Stack } from "@mui/material"
import { ScriptFormData } from "@pages/main/scripts"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { OneTimesType } from "__generated__/graphql"
import { useState } from "react"
import * as yup from "yup"

type AddFeatureDialogProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

export default function AddFeatureDialog({
  isOpen,
  setIsOpen
}: AddFeatureDialogProps) {
  const { translate } = useTranslate()
  const [loading, setLoading] = useState(false)
  const schema = yup
    .object({
      fieldName: yup.string().required(),
      plan: yup.string().required(),
      feature: yup.boolean().required()
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { isValid }
  } = useControlledForm<ScriptFormData>({
    resolver: yupResolver(schema),
    mode: "all"
  })

  const setClose = () => {
    setIsOpen(false)
  }

  const handleClose = () => {
    setIsOpen(false)
    reset()
  }

  async function onSubmit(data: ScriptFormData) {
    setLoading(true)
    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await oneTimesUpdate({
      type: OneTimesType.AddToCustomerProduct,
      metaData: {
        fieldName: data.fieldName,
        plan: data.plan,
        feature: data.feature
      }
    })
    if (!isSuccess || !responseData) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }

    showSuccessToast(translate("succesfully_added"))
    setLoading(false)
    handleClose()
  }
  return (
    <DialogProvider
      title={translate("products_add_new_feature")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("create"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <TextInput<ScriptFormData>
          label={translate("field_name")}
          disabled={loading}
          name="fieldName"
          placeholder={translate("enter")}
          onChange={(e) => {
            setValue("fieldName", e.target.value)
            trigger("fieldName")
          }}
          control={control}
          required
        />
        <TextInput<ScriptFormData>
          type="select"
          label={translate("feature")}
          disabled={loading}
          name="feature"
          control={control}
          placeholder={translate("select")}
          onChange={(e) => {
            setValue("feature", Boolean(e.target.value))
            trigger("feature")
          }}
          selectOptions={[
            { label: translate("yes"), value: true },
            { label: translate("no"), value: false }
          ]}
          required
        />
        <TextInput<ScriptFormData>
          type="select"
          label={translate("plan")}
          disabled={loading}
          name="plan"
          control={control}
          onChange={(e) => {
            setValue("plan", String(e.target.value))
            trigger("plan")
          }}
          placeholder={translate("select")}
          selectOptions={[
            { label: "FreeTrial", value: "FreeTrial" },
            { label: "Start", value: "Start" },
            { label: "Grow", value: "Grow" },
            { label: "Scale", value: "Scale" },
            { label: "Special", value: "Special" },
            { label: "All", value: "All" }
          ]}
          required
        />
      </Stack>
    </DialogProvider>
  )
}
